import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import CustomModal from "../../common/CustomModal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { DatePicker } from "../../ui/date-picker";
import { Label } from "../../ui/label";
import AdsAPIs from "../../../apiRoutes/Ads";
import { GET_AGENCY_SPRINTS_QUERY_KEY, GET_AD_REQUESTS_QUERY_KEY } from "../../../utils/constants/queryKeys";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import toast from "react-hot-toast";

const AssignSprintModal = ({ open, onClose, requestIds }) => {
  const [sprintId, setSprintId] = useState("");
  const [newSprint, setNewSprint] = useState({ delivery_date: null });
  const [sprintFile, setSprintFile] = useState(null);
  const [useNew, setUseNew] = useState(false);
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();

  const { data: sprintResult, isLoading: sprintsLoading } = useQuery({
    queryKey: GET_AGENCY_SPRINTS_QUERY_KEY,
    queryFn: () => AdsAPIs.listSprints({ axiosPrivate, limit: 100, offset: 0 }),
    enabled: open,
  });
  const sprints = sprintResult?.data ?? [];
  const hasSprints = sprints.length > 0;

  useEffect(() => {
    if (open && !sprintsLoading && !hasSprints) setUseNew(true);
  }, [open, sprintsLoading, hasSprints]);

  useEffect(() => {
    if (!open) {
      setSprintFile(null);
      setNewSprint({ delivery_date: null });
    }
  }, [open]);

  const { mutateAsync: createSprint, isPending: creatingSprint } = useMutation({
    mutationFn: (payload) => AdsAPIs.createSprint({ axiosPrivate, payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GET_AGENCY_SPRINTS_QUERY_KEY });
    },
  });

  const { mutate: assign, isPending: assigning } = useMutation({
    mutationFn: (sid) =>
      Promise.all(
        requestIds.map((id) =>
          AdsAPIs.updateAdRequest({ axiosPrivate, id, payload: { ad_sprint_plan_id: Number(sid) } })
        )
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GET_AD_REQUESTS_QUERY_KEY });
      toast.success("Sprint assigned.");
      onClose();
    },
  });

  const handleConfirm = async () => {
    if (useNew) {
      if (!sprintFile) return toast.error("Upload a file for the sprint.");
      try {
        const assetUrl = await AdsAPIs.uploadFile({ axiosPrivate, file: sprintFile });
        if (!assetUrl) return toast.error("Upload failed.");
        const data = await createSprint({
          filename: sprintFile.name,
          asset_url: assetUrl,
          delivery_date: newSprint.delivery_date ? format(newSprint.delivery_date, "yyyy-MM-dd") : undefined,
        });
        if (data?.id) assign(Number(data.id));
      } catch (_) {
        toast.error("Upload or create failed.");
      }
      return;
    }
    if (!sprintId) return toast.error("Select a sprint.");
    assign(Number(sprintId));
  };

  const isPending = creatingSprint || assigning;

  return (
    <CustomModal
      open={open}
      title="Assign to sprint"
      onClose={onClose}
      handleConfirmation={handleConfirm}
      handleConfirmationBtnText="Assign"
      isLoading={isPending}
    >
      <div className="py-1">
        <p className="text-sm text-muted-foreground mb-4">{requestIds?.length ?? 0} request(s) selected.</p>
        <div className="flex gap-4 mb-4">
          {hasSprints && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={!useNew} onChange={() => setUseNew(false)} className="text-primary border-input" />
              <span className="text-sm font-medium">Existing sprint</span>
            </label>
          )}
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" checked={useNew} onChange={() => setUseNew(true)} className="text-primary border-input" />
            <span className="text-sm font-medium">New sprint</span>
          </label>
        </div>
        {!useNew ? (
          <div className="space-y-2">
            <Label>Sprint</Label>
            <Select value={sprintId} onValueChange={setSprintId} disabled={sprintsLoading}>
              <SelectTrigger>
                <SelectValue placeholder="Select sprint" />
              </SelectTrigger>
              <SelectContent>
                {(sprints ?? []).map((s) => (
                  <SelectItem key={s.id} value={String(s.id)}>
                    {s.filename || `Sprint #${s.id}`} {s.delivery_date ? `(${s.delivery_date})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>File</Label>
              <div className="flex gap-2 items-center flex-wrap">
                <label className="inline-flex h-9 items-center justify-center rounded-md border border-input bg-background px-3 text-sm font-medium shadow-sm hover:bg-accent hover:text-accent-foreground cursor-pointer">
                  Choose file
                  <input
                    type="file"
                    className="sr-only"
                    onChange={(e) => setSprintFile(e.target.files?.[0] ?? null)}
                  />
                </label>
                {sprintFile && (
                  <span className="text-sm text-gray-600 truncate max-w-[200px]" title={sprintFile.name}>
                    {sprintFile.name}
                  </span>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Delivery date</Label>
              <DatePicker value={newSprint.delivery_date} onChange={(d) => setNewSprint((s) => ({ ...s, delivery_date: d }))} placeholder="Pick a date" />
            </div>
          </div>
        )}
      </div>
    </CustomModal>
  );
};

export default AssignSprintModal;
